import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import {
  DownloadSimple, PencilSimpleLine, Receipt, Sliders, PlugsConnected, Buildings, Question,
  ArrowUUpLeft, PlusCircle, Lightning, MagnifyingGlass, X, FilePdf, MicrosoftExcelLogo, Lock,
  Trash, Broom,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABELS = { PREPAYMENT: "Prepayments", ACCRUAL: "Accruals", DEFERRED_INCOME: "Deferred Income", DEPRECIATION: "Depreciation" };
const VALID_TYPES = ["PREPAYMENT", "ACCRUAL", "DEFERRED_INCOME", "DEPRECIATION"];

const ICONS = {
  IMPORT: DownloadSimple, CREATE_SCHEDULE: PencilSimpleLine, POST_JOURNAL: Receipt,
  UPDATE_SETTINGS: Sliders, SELECT_ORGS: Buildings, DISCONNECT: PlugsConnected,
  REVERSE_SCHEDULE: ArrowUUpLeft, CREATE_ADJUSTMENT: PlusCircle, RELEASE_FULL: Lightning,
  RETIRE_SCHEDULE: Trash, PURGE_PENDING_GHOSTS: Broom, UNDO_PURGE_PENDING_GHOSTS: ArrowUUpLeft,
  POST_JOURNAL_BLOCKED: Broom,
};

// Map each action to a filter group.
const GROUP_OF = {
  CREATE_ADJUSTMENT: "adjustments",
  REVERSE_SCHEDULE: "reversals", DELETE_SCHEDULE: "reversals",
  CREATE_SCHEDULE: "schedules", RELEASE_FULL: "schedules", AMEND_SCHEDULE: "schedules", UPDATE_SCHEDULE: "schedules",
  POST_JOURNAL: "journals", POST_JOURNAL_FAILED: "journals",
  RETIRE_SCHEDULE: "cleanup", PURGE_PENDING_GHOSTS: "cleanup", UNDO_PURGE_PENDING_GHOSTS: "cleanup",
  POST_JOURNAL_BLOCKED: "autocancelled",
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "adjustments", label: "Adjustments" },
  { id: "reversals", label: "Reversals" },
  { id: "schedules", label: "Schedules" },
  { id: "journals", label: "Journals" },
  { id: "cleanup", label: "Cleanup" },
  { id: "autocancelled", label: "Auto-cancelled" },
  { id: "other", label: "Other" },
];

const groupOf = (action) => GROUP_OF[action] || "other";

const fmtChange = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("en-GB");
  return String(v);
};

export default function Audit() {
  const [searchParams] = useSearchParams();
  const lockedType = VALID_TYPES.includes(searchParams.get("type")) ? searchParams.get("type") : null;
  const initialFilter = FILTERS.some((f) => f.id === searchParams.get("filter")) ? searchParams.get("filter") : "all";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [pdfing, setPdfing] = useState(false);
  const [exceling, setExceling] = useState(false);
  const [allOrgs, setAllOrgs] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (lockedType) params.set("type", lockedType);
    if (allOrgs) params.set("all_orgs", "true");
    setLoading(true);
    api.get(`/audit?${params.toString()}`).then(({ data }) => setLogs(data.logs)).finally(() => setLoading(false));
  }, [lockedType, allOrgs]);

  // Mark the audit history as viewed for this org so the "recently removed" nav badge clears.
  useEffect(() => {
    api.post("/audit/mark-viewed").then(() => window.dispatchEvent(new Event("badges:refresh"))).catch(() => {});
  }, []);

  const users = useMemo(() => [...new Set(logs.map((l) => l.user_email).filter(Boolean))].sort(), [logs]);

  const exportPdf = () => exportFile("pdf");
  const exportExcel = () => exportFile("xlsx");

  const exportFile = async (ext) => {
    ext === "pdf" ? setPdfing(true) : setExceling(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (userFilter) params.set("user_email", userFilter);
      if (filter !== "all") params.set("group", filter);
      if (search.trim()) params.set("q", search.trim());
      if (lockedType) params.set("type", lockedType);
      if (allOrgs) params.set("all_orgs", "true");
      const path = ext === "pdf" ? "/audit/pdf" : "/audit/excel";
      const mime = ext === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const res = await api.get(`${path}?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-report-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the activity report");
    } finally { setPdfing(false); setExceling(false); }
  };

  const counts = useMemo(() => {
    const c = { all: logs.length };
    for (const l of logs) { const g = groupOf(l.action); c[g] = (c[g] || 0) + 1; }
    return c;
  }, [logs]);

  const q = search.trim().toLowerCase();
  const filtered = logs.filter((l) => {
    const ts = (l.ts || "").slice(0, 10);
    return (filter === "all" || groupOf(l.action) === filter) &&
      (!start || ts >= start) &&
      (!end || ts <= end) &&
      (!userFilter || l.user_email === userFilter) &&
      (!q || (l.detail || "").toLowerCase().includes(q) ||
        (l.action || "").toLowerCase().includes(q) ||
        (l.user_email || "").toLowerCase().includes(q));
  });

  return (
    <Page>
      <PageHeader overline="Compliance" title={lockedType ? `${TYPE_LABELS[lockedType]} · Activity` : "Audit History"}
        subtitle="Every import, schedule, adjustment, reversal and posted journal, with timestamps and Xero references.">
        {lockedType && (
          <span data-testid="type-lock-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-orangeink bg-orange/10 border border-orange/30 px-2.5 py-1 rounded-lg">
            <Lock size={12} weight="fill" /> {TYPE_LABELS[lockedType]} only
          </span>
        )}
        <button onClick={exportPdf} disabled={pdfing} data-testid="audit-pdf-btn"
          className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orange transition-colors duration-200 disabled:opacity-60">
          <FilePdf size={18} /> {pdfing ? "BUILDING…" : "PDF"}
        </button>
        <button onClick={exportExcel} disabled={exceling} data-testid="audit-excel-btn"
          className="flex items-center gap-2 bg-white border border-positive/40 text-positive px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-positive hover:text-white transition-colors duration-200 disabled:opacity-60">
          <MicrosoftExcelLogo size={18} /> {exceling ? "BUILDING…" : "Excel"}
        </button>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-x-5 gap-y-3 mb-4 bg-white border border-slate-200 rounded-lg p-4">
        <div>
          <span className="overline text-navy/65 block mb-1">From</span>
          <input type="date" value={start} data-testid="audit-start" onChange={(e) => setStart(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-mono text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
        </div>
        <div>
          <span className="overline text-navy/65 block mb-1">To</span>
          <input type="date" value={end} data-testid="audit-end" onChange={(e) => setEnd(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-mono text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
        </div>
        <div>
          <span className="overline text-navy/65 block mb-1">User</span>
          <select value={userFilter} data-testid="audit-user" onChange={(e) => setUserFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none min-w-[180px]">
            <option value="">All users</option>
            {users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        {(start || end || userFilter) && (
          <button onClick={() => { setStart(""); setEnd(""); setUserFilter(""); }} data-testid="audit-clear-range"
            className="text-xs text-orangeink hover:underline pb-1.5">Clear</button>
        )}
        <div className="ml-auto">
          <span className="overline text-navy/65 block mb-1">Scope</span>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="audit-scope-toggle">
            <button onClick={() => setAllOrgs(false)} data-testid="audit-scope-this"
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${!allOrgs ? "bg-navy text-white" : "bg-white text-navy/70 hover:bg-slate-50"}`}>
              This organisation
            </button>
            <button onClick={() => setAllOrgs(true)} data-testid="audit-scope-all"
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${allOrgs ? "bg-navy text-white" : "bg-white text-navy/70 hover:bg-slate-50"}`}>
              All organisations
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`audit-filter-${f.id}`}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                filter === f.id ? "bg-orange text-white border-orange" : "bg-white text-navy/70 border-slate-200 hover:border-navy/40"
              }`}>
              {f.label}{counts[f.id] ? <span className="ml-1.5 opacity-60">{counts[f.id]}</span> : null}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/35" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search"
            placeholder="Search detail, user or action…"
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-8 py-1.5 text-xs text-navy placeholder:text-navy/35 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-colors" />
          {search && (
            <button onClick={() => setSearch("")} data-testid="audit-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy/35 hover:text-navy">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        {filtered.map((l) => {
          const Icon = ICONS[l.action] || Question;
          const grp = groupOf(l.action);
          const isReversal = grp === "reversals";
          const isCleanup = grp === "cleanup";
          const isAutoCancel = grp === "autocancelled";
          const tintBox = isReversal ? "bg-negative/10 text-negative"
            : isCleanup ? "bg-warn/10 text-warn"
            : isAutoCancel ? "bg-navy/10 text-navy" : "bg-orange/10 text-orangeink";
          const tintLabel = isReversal ? "text-negative" : isCleanup ? "text-warn"
            : isAutoCancel ? "text-navy" : "text-navy";
          return (
            <div key={l.id} data-testid={`audit-row-${l.id}`}
              className="flex gap-4 px-6 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tintBox}`}>
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${tintLabel}`}>
                    {l.action.replace(/_/g, " ")}
                  </span>
                  <span className="text-xs text-navy/65 font-mono">· {l.entity_type}</span>
                </div>
                <div className="text-sm text-navy/70 mt-0.5">{l.detail}</div>
                {Array.isArray(l.changes) && l.changes.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`audit-changes-${l.id}`}>
                    {l.changes.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-[0.7rem] font-mono bg-navy/[0.04] border border-slate-200 rounded px-1.5 py-0.5">
                        <span className="text-navy/65">{c.field.replace(/_/g, " ")}</span>
                        <span className="text-negative line-through">{fmtChange(c.from)}</span>
                        <span className="text-navy/35">→</span>
                        <span className="text-positive font-medium">{fmtChange(c.to)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-xs text-navy/70 tabular">{fmtDate(l.ts)}</div>
                <div className="font-mono text-[0.65rem] text-navy/65">{new Date(l.ts).toLocaleTimeString("en-GB")}</div>
                <div className="text-[0.65rem] text-navy/65 mt-0.5">{l.user_email}</div>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="audit-empty">
            {logs.length === 0 ? "No activity recorded yet." : "No matching activity."}
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-navy/65 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>
    </Page>
  );
}
